import React, { useState, useEffect } from "react";
import { ReactComponent as FeedbackIcon } from "../../../../assets/icons/svg/Dashboard/Total_feedback.svg";
import { ReactComponent as ScreenshotIcon } from "../../../../assets/icons/svg/Dashboard/Total_Screenshot.svg";
import { ReactComponent as RecordingIcon } from "../../../../assets/icons/svg/Dashboard/Total_Recording.svg";
import UserDetailPopup from "../../../ui/UserDetailPopup";
import { API_URLS } from "../../../api/urls";
import AuthUtils from "../../../utils/auth.utils";

interface User {
  user_id: number;
  name: string;
  role: string;
  email: string;
  joined: string;
  status: "Active" | "In-active" | "DNS";
  profile_image: string | null;
  avatar: string;
}

interface UserDetails {
  user_id: number;
  name: string;
  role: string;
  email: string;
  profile_image: string | null;
  workspace_name: string;
  total_cards_created: number;
  total_feedback: number;
  total_screenshots: number;
  total_recordings: number;
  last_active: string;
  joined?: string;
  status?: "Active" | "In-active" | "DNS";
  avatar?: string;
}

interface DashboardData {
  userName: string;
  totalUsers: number;
  newUsers: number;
  totalFeedback: number;
  totalScreenshots: number;
  totalRecordings: number;
  users: User[];
}

interface DashboardPageProps {
  adminName?: string;
  adminLoading?: boolean;
}

const DashboardPage: React.FC<DashboardPageProps> = ({ adminName, adminLoading }) => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupLoading, setPopupLoading] = useState(false);

  // Format date to match the existing format
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  // Get profile image URL helper
  const getProfileImage = (profileImage: string | null) => {
    if (!profileImage) return null;
    // If it's already a full URL, return it
    if (profileImage.startsWith('http')) return profileImage;
    // Otherwise, prepend the base URL
    return `https://api.feebdack.in${profileImage}`;
  };

  // Generate avatar emoji based on user name
  const getAvatarEmoji = (name: string) => {
    const emojis = ['👨‍💼', '👩‍💼', '👨‍🦰', '👩‍🦰', '👨‍🦱', '👩‍🦱', '👨', '👩'];
    const index = name.charCodeAt(0) % emojis.length;
    return emojis[index];
  };

  // Fetch user details for popup
  const fetchUserDetails = async (userId: number) => {
    try {
      setPopupLoading(true);
      const response = await fetch(API_URLS.GET_USER_DETAILS(userId));
      const apiData = await response.json();

      if (apiData.success === 1) {
        const userDetails: UserDetails = {
          user_id: apiData.user.user_id,
          name: apiData.user.name,
          email: apiData.user.email,
          role: apiData.user.role,
          profile_image: getProfileImage(apiData.user.profile_image),
          workspace_name: apiData.user.workspace_name,
          total_cards_created: apiData.stats.total_cards_created,
          total_feedback: apiData.stats.total_feedback,
          total_screenshots: apiData.stats.total_screenshots,
          total_recordings: apiData.stats.total_recordings,
          last_active: formatDate(apiData.stats.last_active),
          avatar: getAvatarEmoji(apiData.user.name)
        };
        
        setSelectedUser(userDetails);
      } else {
        console.error('Failed to load user details');
      }
    } catch (err) {
      console.error('Error fetching user details:', err);
    } finally {
      setPopupLoading(false);
    }
  };

  const handleUserClick = async (user: User) => {
    // Set initial data from the table row
    setSelectedUser({
      user_id: user.user_id,
      name: user.name,
      email: user.email,
      role: user.role,
      profile_image: user.profile_image,
      workspace_name: "myworks", // Default value
      total_cards_created: 0,
      total_feedback: 0,
      total_screenshots: 0,
      total_recordings: 0,
      last_active: user.joined,
      avatar: user.avatar
    });
    setIsPopupOpen(true);
    
    // Fetch detailed data in background
    await fetchUserDetails(user.user_id);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Get admin_id from session
        const adminId = AuthUtils.getAdminId();
        
        if (!adminId) {
          setError('Session expired. Please login again.');
          setLoading(false);
          return;
        }

        const response = await fetch(API_URLS.GET_DASHBOARD_DETAILS(Number(adminId)));
        const apiData = await response.json();

        console.log('Dashboard API Response:', apiData);

        if (apiData.success === 1) {
          const displayName = adminName || "Admin User";
          console.log('Using admin name:', displayName);

          setDashboardData({
            userName: displayName,
            totalUsers: apiData.data.total_users,
            newUsers: apiData.data.new_users_today,
            totalFeedback: apiData.data.total_feedback,
            totalScreenshots: apiData.data.total_screenshots,
            totalRecordings: apiData.data.total_recordings,
            users: apiData.data.latest_users.map((user: any) => ({
              user_id: user.user_id,
              name: user.name,
              role: user.role,
              email: user.email,
              joined: formatDate(user.joined),
              status: user.status,
              profile_image: getProfileImage(user.profile_image),
              avatar: getAvatarEmoji(user.name)
            }))
          });
        } else {
          setError('Failed to load dashboard data');
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Error loading dashboard data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch dashboard data when admin name is loaded or after a short delay
    if (!adminLoading) {
      fetchDashboardData();
    }
  }, [adminName, adminLoading]);

  if (loading || adminLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Failed to load dashboard'}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  console.log('Rendering with userName:', dashboardData.userName);

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-white min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
        <div className="lg:col-span-5 bg-[#050505] rounded-3xl p-6 sm:p-6 text-white">
          <h1 className="text-base sm:text-lg 3xl:text-xl font-medium font-inter mb-4 sm:mb-4 lg:mb-6">
            Hello,{" "}
            <span className="font-semibold">{dashboardData.userName}</span>
          </h1>

          <div className="flex items-center justify-center mx-6 xl:mx-12 gap-12 sm:gap-32 lg:gap-12 xl:gap-20 2xl:gap-28 4xl:gap-40">
            <div className="flex flex-col items-center">
              <div className="text-[28px] sm:text-[32px] lg:text-[32px] 2xl:text-[36px] font-bold mb-1">
                {dashboardData.totalUsers}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">Total user</div>
            </div>

            <div className="h-16 sm:h-20 w-px bg-gray-700 mb-2"></div>

            <div className="flex flex-col items-center">
              <div className="text-[28px] sm:text-[32px] lg:text-[32px] 2xl:text-[36px] font-bold mb-1">
                {dashboardData.newUsers}
              </div>
              <div className="text-gray-400 text-xs sm:text-sm">New user</div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div className="bg-white rounded-3xl py-4 sm:py-6 lg:py-6 px-4 sm:px-6 lg:px-6 xl:px-8 border border-[#CFCFCF]">
            <div className=" mb-3 sm:mb-4 lg:mb-6 xl:mb-10 2xl:mb-12 4xl:mb-14">
               <FeedbackIcon className={`w-5 h-5 sm:w-6 sm:h-6 xl:w-8 xl:h-8  `} />
            </div>
            <div className="text-xl sm:text-2xl font-semibold font-inter text-[#111C2D] mb-1">
              {dashboardData.totalFeedback}
            </div>
            <div className="text-[#111C2D99] text-xs sm:text-sm">
              Total feedback
            </div>
          </div>

          <div className="bg-white rounded-3xl py-4 sm:py-6 lg:py-6 px-4 sm:px-6 lg:px-6 xl:px-8 border border-[#CFCFCF]">
            <div className=" mb-3 sm:mb-4 lg:mb-6 xl:mb-10 2xl:mb-12 4xl:mb-14">
               <ScreenshotIcon className={`w-5 h-5 sm:w-6 sm:h-6 xl:w-8 xl:h-8  `} />
            </div>
            <div className="text-xl sm:text-2xl font-semibold font-inter text-[#111C2D] mb-1">
              {dashboardData.totalScreenshots}
            </div>
            <div className="text-[#111C2D99] text-xs sm:text-sm">
              Total screenshots
            </div>
          </div>

          <div className="bg-white rounded-3xl py-4 sm:py-6 lg:py-6 px-4 sm:px-6 lg:px-6 xl:px-8 border border-[#CFCFCF]">
            <div className="mb-3 sm:mb-4 lg:mb-6 xl:mb-10 2xl:mb-12 4xl:mb-14">
               <RecordingIcon className={`w-5 h-5 sm:w-6 sm:h-6 xl:w-8 xl:h-8 `} />
            </div>
            <div className="text-xl sm:text-2xl font-semibold font-inter text-[#111C2D] mb-1">
              {dashboardData.totalRecordings}
            </div>
            <div className="text-[#111C2D99] text-xs sm:text-sm">
              Total recordings
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-3xl border border-[#CFCFCF] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-[#CFCFCF] bg-[#F3F5F8]">
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base">
                  Name
                </th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base">
                  Role
                </th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base">
                  Email
                </th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base">
                  Joined
                </th>
                <th className="text-left py-3 sm:py-4 px-4 sm:px-6 font-semibold text-gray-900 text-sm sm:text-base">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {dashboardData.users.map((user, index) => (
                <tr
                  key={index}
                  onClick={() => handleUserClick(user)}
                  className={`${
                    index !== dashboardData.users.length - 1
                      ? "border-b border-gray-100"
                      : ""
                  } hover:bg-gray-50 transition-colors cursor-pointer`}
                >
                  <td className="py-3 sm:py-4 px-4 sm:px-6">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-orange-200 to-orange-300 flex items-center justify-center text-xl sm:text-2xl flex-shrink-0 overflow-hidden">
                        {user.profile_image ? (
                          <img 
                            src={user.profile_image} 
                            alt={user.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              // Fallback to emoji if image fails to load
                              e.currentTarget.style.display = 'none';
                              e.currentTarget.parentElement!.textContent = user.avatar || '👤';
                            }}
                          />
                        ) : (
                          user.avatar || '👤'
                        )}
                      </div>
                      <span className="font-semibold text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter">
                        {user.name}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 sm:py-4 px-4 sm:px-6 text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter ">
                    {user.role}
                  </td>
                  <td className="py-3 sm:py-4 px-4 sm:px-6 text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter">
                    {user.email}
                  </td>
                  <td className="py-3 sm:py-4 px-4 sm:px-6 text-[#111C2D] text-sm sm:text-base lg:text-sm 2xl:text-base font-inter">
                    {user.joined}
                  </td>
                  <td className="py-3 sm:py-4 px-4 sm:px-6">
                    <span
                      className={`inline-flex items-center px-3 sm:px-4 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm font-medium border ${
                        user.status === "Active"
                          ? " text-[#1EAB1E] border-[#1EAB1E]"
                          : user.status === "In-active"
                          ? " text-[#E2941F] border-[#E2941F]"
                          : " text-[#804BE9] border-[#804BE9]"
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Detail Popup */}
      <UserDetailPopup
        user={selectedUser}
        isOpen={isPopupOpen}
        onClose={handleClosePopup}
        loading={popupLoading}
      />
    </div>
  );
};

export default DashboardPage;